// GHS 그림문자 이미지 매칭에 쓰는 해시 함수 모음.
// 빌드 스크립트(scripts/build-ghs-hashes.js, Node/sharp)와 런타임 매처(pictogramImageMatcher.js,
// 브라우저)가 동일한 함수를 공유해야 같은 입력에 대해 항상 같은 해시가 나온다 — 리샘플링 로직이
// 둘 중 한쪽에만 있으면(예: 한쪽만 canvas로 리사이즈) 커널 차이로 미세하게 다른 비트가 나올 수 있다.
// 이 저장소의 다른 소스 파일과 동일하게 ES 모듈로 작성한다. webpack(import)에서는 그대로 쓰고,
// Node 빌드 스크립트에서는 @babel/core 로 즉석 변환해서 쓴다(module.exports 를 직접 쓰면 babel이
// 이 파일을 ES 모듈로 파싱하면서 만드는 interop 코드와 충돌해 "read only property 'exports'"
// 런타임 에러가 난다 — 실제로 겪은 문제).

// 실측(샘플 MSDS PDF 1건 + GHS 웹앱 에셋 9종) 기준으로 확정한 값.
// CROP: 마름모 바운딩박스 기준 비율 크롭. 크롭 없이(bbox만)는 레퍼런스 간 최소거리가 42까지
// 떨어져 공용 빨간 테두리가 비트 대부분을 차지하는 문제가 있었다 — 이 CROP으로 201까지 벌어짐.
export const CROP = [0.3, 0.28, 0.7, 0.76];
export const GRID = 32;
export const THRESHOLD = 128;
// 레퍼런스 간 최소거리(201, GHS01-GHS04)의 절반. 이 값 이하이면서 2등과 MIN_MARGIN 이상
// 벌어져야 "확신 있는 매칭"으로 인정한다 — 구조적으로 오분류가 나오기 어려운 여유치다.
// 120 으로 올려 본 실측: 그림문자가 없는 MSDS(증류수)의 장식 이미지가 GHS01 에 108/격차 77 로
// 잘못 잡혔다. 100~120 구간은 오탐이 섞이므로 올리지 말 것.
export const MAX_DISTANCE = 100;
export const MIN_MARGIN = 70;

function pixelIndex(y, x, width, channels) {
  return ((y * width) + x) * channels;
}

function luminanceAt(data, i) {
  const r = data[i];
  const g = data[i + 1];
  const b = data[i + 2];
  return (0.299 * r) + (0.587 * g) + (0.114 * b);
}

// 연속 좌표 (fx, fy) 의 밝기를 주변 4픽셀로 양선형 보간한다(픽셀 중심을 정수+0.5 로 본다).
function luminanceBilinear(data, width, height, channels, fx, fy) {
  const x = Math.min(Math.max(fx - 0.5, 0), width - 1);
  const y = Math.min(Math.max(fy - 0.5, 0), height - 1);
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.min(x0 + 1, width - 1);
  const y1 = Math.min(y0 + 1, height - 1);
  const tx = x - x0;
  const ty = y - y0;
  const top = (luminanceAt(data, pixelIndex(y0, x0, width, channels)) * (1 - tx)) + (luminanceAt(data, pixelIndex(y0, x1, width, channels)) * tx);
  const bottom = (luminanceAt(data, pixelIndex(y1, x0, width, channels)) * (1 - tx)) + (luminanceAt(data, pixelIndex(y1, x1, width, channels)) * tx);
  return (top * (1 - ty)) + (bottom * ty);
}

// 셀 한 변이 이 픽셀 수보다 작은 저해상도 이미지는 픽셀 박스 평균 대신 양선형 보간 슈퍼샘플링으로 셀 값을 구한다.
// 실측(57px 그림문자, 960px 에셋을 57px 로 줄인 9종): 박스 평균 77~163 → 보간 45~105 로 거리가 크게 준다.
// 큰 이미지(셀 ≥ 4px)는 기존 박스 평균 그대로라 레퍼런스 해시와 기존 매칭 결과가 바뀌지 않는다.
export const BBOX_THRESHOLD = 225;
const SMOOTH_MAX_CELL = 4;
const SMOOTH_SAMPLES = 4; // 셀당 4x4 샘플

// 마름모의 바운딩박스를 찾는다(흰 배경이 아닌 픽셀 기준). 에셋마다 여백/해상도가 달라도
// 이 기준으로 크롭하면 흡수된다. 입력은 이미 흰 배경 위에 합성된 RGB(또는 RGBA) 픽셀이어야 한다.
export function bbox(data, width, height, channels, bgThreshold = 245) {
  let x0 = width;
  let y0 = height;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const lum = luminanceAt(data, pixelIndex(y, x, width, channels));
      if (lum < bgThreshold) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < 0) return { x: 0, y: 0, w: width, h: height };
  return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

// { data, width, height, channels } 형태의 흰 배경 합성 픽셀 → GRID x GRID 이진 문자열.
// 원본 해상도 그대로 셀 단위 박스 평균을 내려 축소하므로, 별도의 리사이즈(canvas resize 등)를
// 거치지 않아도 항상 같은 결과가 나온다.
export function toBits(pixels, opts = {}) {
  const grid = opts.grid || GRID;
  const threshold = opts.threshold || THRESHOLD;
  const crop = opts.crop || CROP;
  const { data, width, height, channels } = pixels;

  // opts.shift = [dx, dy] (그리드 셀 단위): 크롭 창을 그만큼 옮겨서 해시한다. 작은 이미지(100px 안팎)는
  // 마름모 테두리의 안티앨리어싱 때문에 bbox 가 1~2px 어긋나 그리드가 한 칸 밀리고, 그 한 칸 차이가
  // 해밍 거리 100 이상으로 커진다. 매처는 ±1 칸 이동한 해시 중 최소 거리를 쓴다(pictogramImageMatcher).
  const [sx, sy] = opts.shift || [0, 0];
  // bbox 는 245 보다 낮은 225 를 쓴다: JPEG 로 저장된 그림문자는 마름모 둘레에 옅은 회색 번짐이 있어
  // 245 기준이면 bbox 가 번짐까지 포함해 커지고, 크롭 창이 밀려 거리가 148 까지 벌어졌다(실측: 암모니아수 MSDS 148→55).
  // 215 이하로 내리면 반대로 57px 급 작은 이미지의 안티앨리어싱 된 꼭짓점이 잘려 거리가 나빠진다(실측 74→172).
  const rawBox = bbox(data, width, height, channels, opts.bboxThreshold || BBOX_THRESHOLD);
  const [rx0, ry0, rx1, ry1] = crop;
  const box = {
    x: rawBox.x + Math.round(rawBox.w * rx0),
    y: rawBox.y + Math.round(rawBox.h * ry0),
    w: Math.round(rawBox.w * (rx1 - rx0)),
    h: Math.round(rawBox.h * (ry1 - ry0)),
  };
  box.x += Math.round((sx * box.w) / grid);
  box.y += Math.round((sy * box.h) / grid);

  const bits = new Uint8Array(grid * grid);
  const smooth = box.w / grid < SMOOTH_MAX_CELL || box.h / grid < SMOOTH_MAX_CELL;
  for (let gy = 0; gy < grid; gy++) {
    for (let gx = 0; gx < grid; gx++) {
      if (smooth) {
        let sum = 0;
        for (let sy = 0; sy < SMOOTH_SAMPLES; sy++) {
          for (let sx = 0; sx < SMOOTH_SAMPLES; sx++) {
            const fx = box.x + (((gx + ((sx + 0.5) / SMOOTH_SAMPLES)) * box.w) / grid);
            const fy = box.y + (((gy + ((sy + 0.5) / SMOOTH_SAMPLES)) * box.h) / grid);
            sum += luminanceBilinear(data, width, height, channels, fx, fy);
          }
        }
        bits[(gy * grid) + gx] = sum / (SMOOTH_SAMPLES * SMOOTH_SAMPLES) < threshold ? 1 : 0;
        continue;
      }
      const x0 = box.x + Math.floor((gx * box.w) / grid);
      const y0 = box.y + Math.floor((gy * box.h) / grid);
      // 크롭 영역이 그리드보다 작은 저해상도 이미지(실측: 57px 그림문자, 크롭 약 22px)는 셀 폭이 1px 미만이라
      // 비어 있는 셀이 생기고, 빈 셀은 무조건 0 이 되어 해시가 망가진다. 셀마다 최소 1px 은 보게 한다.
      const x1 = Math.max(x0 + 1, box.x + Math.floor(((gx + 1) * box.w) / grid));
      const y1 = Math.max(y0 + 1, box.y + Math.floor(((gy + 1) * box.h) / grid));
      let sum = 0;
      let n = 0;
      for (let y = Math.max(0, y0); y < Math.min(height, y1); y++) {
        for (let x = Math.max(0, x0); x < Math.min(width, x1); x++) {
          sum += luminanceAt(data, pixelIndex(y, x, width, channels));
          n++;
        }
      }
      bits[(gy * grid) + gx] = n && sum / n < threshold ? 1 : 0;
    }
  }
  return Array.from(bits).join("");
}

export function hamming(a, b) {
  let d = 0;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) d++;
  return d;
}

// pdf.js 가 주는 이미지(RGBA 32bpp 또는 RGB 24bpp, kind: ImageKind)를 흰 배경 위에 합성한다.
// canvas 없이 순수 계산으로 처리한다 — 브라우저/Node/워커 어디서도 동일하게 동작하고,
// alpha=0 인 픽셀의 원본 RGB가 검정이어도(투명 영역이 검정 마스크인 경우) 결과에 영향이 없다.
// kind=3(RGBA_32BPP), kind=2(RGB_24BPP)만 지원한다 — 그 외(1bpp 등)는 null 을 반환하니 호출부에서 건너뛴다.
export function compositeToWhiteRGB(width, height, kind, data) {
  const pixelCount = width * height;
  const out = new Uint8ClampedArray(pixelCount * 3);

  if (kind === 3) {
    for (let p = 0; p < pixelCount; p++) {
      const srcOffset = p * 4;
      const dstOffset = p * 3;
      const alpha = data[srcOffset + 3] / 255;
      const whiteMix = 255 * (1 - alpha);
      out[dstOffset] = (data[srcOffset] * alpha) + whiteMix;
      out[dstOffset + 1] = (data[srcOffset + 1] * alpha) + whiteMix;
      out[dstOffset + 2] = (data[srcOffset + 2] * alpha) + whiteMix;
    }
  } else if (kind === 2) {
    for (let p = 0; p < pixelCount * 3; p++) out[p] = data[p];
  } else if (kind === 1) {
    // 1비트 흑백(ImageKind.GRAYSCALE_1BPP): 행마다 바이트 경계로 채워진 비트열, 비트 1 = 흰색 (pdf.js canvas 렌더와 동일).
    // 흑백 복합기 스캔 MSDS 가 이 형식이다(실측: 2480x3507, 에폭시 레진/경화제).
    const rowBytes = Math.ceil(width / 8);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const bit = data[(y * rowBytes) + (x >> 3)] & (0x80 >> (x & 7));
        const v = bit ? 255 : 0;
        const o = ((y * width) + x) * 3;
        out[o] = v;
        out[o + 1] = v;
        out[o + 2] = v;
      }
    }
  } else {
    return null;
  }
  return { data: out, width, height, channels: 3 };
}
